import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Skeleton,
  Chip,
  Divider,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import Tooltip from "@mui/material/Tooltip";
import useLeague from "../hooks/useLeague/useLeague";
import { LeagueMini } from "../types/league";
import { Link } from "react-router-dom";
import { GetSelectedGamesResponse, SelectedGame } from "../features/cfbd/types/game";
import GameCard from "../features/cfbd/components/GameCard";
import { isGameStarted } from "../features/cfbd/utils/game";
import useScores from "../hooks/useScores/useScores";
import { PlayerScore } from "../features/cfbd/types/score";
import { useAuth0 } from "@auth0/auth0-react";
import useCfbd from "../features/cfbd/hooks/useCfbd/useCfbd";
import { PollsResponse } from "../features/cfbd/types/poll";
import { RecordsResponse } from "../features/cfbd/types/records";
import { getTeamPollRank } from "../features/cfbd/utils/poll";
import { getTeamRecord } from "../features/cfbd/utils/records";
import LeaguePieArc from "../dashboard/LeaguePieArc";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Lightweight horizontal scroller with arrows + smooth wheel-to-horizontal + keyboard support
const HorizontalScroller: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateScrollState();
    const onScroll = () => updateScrollState();
    const onResize = () => updateScrollState();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [updateScrollState]);

  const scrollByAmount = (dir: "left" | "right") => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.max(240, Math.round(el.clientWidth * 0.8));
    el.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  // Improved wheel listener: only hijack when horizontal scrolling is possible.
  // Converts vertical wheel gestures to horizontal movement while allowing page scroll
  // when at extremes or no horizontal overflow. Uses step-based increments for consistency.
  React.useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const wheelHandler = (e: WheelEvent) => {
      const canHorizScroll = el.scrollWidth > el.clientWidth + 2; // allow for minor rounding
      if (!canHorizScroll) return; // let page handle it

      // Determine intended horizontal delta: prefer explicit horizontal (deltaX) or shift+wheel.
      const dominantAxisIsVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX);
      let rawDelta = dominantAxisIsVertical ? e.deltaY : e.deltaX;
      if (e.shiftKey && Math.abs(e.deltaY) > 0) rawDelta = e.deltaY; // shift typically maps to horizontal

      // If vertical gesture and user at start/end and scrolling away from content, allow default.
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      const direction = rawDelta > 0 ? 1 : -1;
      if (dominantAxisIsVertical && ((direction < 0 && atStart) || (direction > 0 && atEnd))) {
        return; // permit normal page vertical scroll
      }

      // We will consume the event.
      e.preventDefault();
      e.stopPropagation();

      // Normalized step (responsive to container width, but capped for large containers)
      const baseStep = Math.max(160, Math.round(el.clientWidth * 0.6));
      // Trackpad fine-grain horizontal gestures keep proportional movement; for large deltas clamp.
      const magnitude = Math.min(Math.abs(rawDelta), baseStep);
      const step = direction * (dominantAxisIsVertical ? baseStep : magnitude);
      el.scrollBy({ left: step, behavior: "smooth" });
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        scrollByAmount("left");
        break;
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        scrollByAmount("right");
        break;
      case "Home":
        e.preventDefault();
        el.scrollTo({ left: 0, behavior: "smooth" });
        break;
      case "End":
        e.preventDefault();
        el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        ref={scrollerRef}
        onKeyDownCapture={onKeyDown}
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          overflowY: "hidden",
          pb: 1,
          scrollSnapType: { xs: "x mandatory", md: "unset" },
          "& > *": { scrollSnapAlign: { xs: "start", md: "unset" } },
          scrollBehavior: "smooth",
          overscrollBehavior: "contain",
          touchAction: "pan-x",
          px: { xs: 0.5, md: 0 },
          outline: "none",
          "&:focus-visible": {
            outline: (t) => `2px solid ${t.palette.primary.main}`,
            outlineOffset: 2,
            borderRadius: 1,
          },
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(255,255,255,0.2)"
                : "rgba(0,0,0,0.2)",
            borderRadius: 999,
          },
        }}
        id="game-cards-scroller"
        tabIndex={0}
        role="region"
        aria-label="Game cards scroller"
        aria-roledescription="Horizontal list of game cards"
        aria-describedby="game-cards-scroller-instructions"
      >
        {/* Visually hidden instructions for screen reader users */}
        <Box
          id="game-cards-scroller-instructions"
          component="p"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(1px, 1px, 1px, 1px)",
            whiteSpace: "nowrap",
          }}
        >
          Use arrow keys, Home/End, PageUp/PageDown, or the scroll buttons to navigate horizontally. The page will scroll vertically when you reach the ends.
        </Box>
        {children}
      </Box>
      {canScrollLeft && (
        <IconButton
          aria-label="scroll left"
          aria-controls="game-cards-scroller"
          size="small"
          onClick={() => { scrollByAmount("left"); scrollerRef.current?.focus(); }}
          sx={{
            position: "absolute",
            left: -8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(0,0,0,0.4)"
                : "rgba(255,255,255,0.8)",
            boxShadow: 1,
            "&:hover": {
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(0,0,0,0.6)"
                  : "rgba(255,255,255,0.9)",
            },
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      )}
      {canScrollRight && (
        <IconButton
          aria-label="scroll right"
          aria-controls="game-cards-scroller"
          size="small"
          onClick={() => { scrollByAmount("right"); scrollerRef.current?.focus(); }}
          sx={{
            position: "absolute",
            right: -8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(0,0,0,0.4)"
                : "rgba(255,255,255,0.8)",
            boxShadow: 1,
            "&:hover": {
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(0,0,0,0.6)"
                  : "rgba(255,255,255,0.9)",
            },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

// (Removed top-level LeagueCard list per product direction)

const FakeData = {
  league(id: string, name: string): LeagueMini {
    return { id, name, created: Date.now(), memberCount: 8 };
  },
  selectedGames(): GetSelectedGamesResponse {
    const now = new Date();
    const iso = new Date(now.getTime() + 10 * 60 * 1000).toISOString();
    return {
      year: now.getFullYear(),
      week: 1,
      ready: true,
      games: [
        {
          game: {
            id: 9001,
            homeTeam: 101,
            awayTeam: 102,
            neutralSite: false,
            line: -3.5,
            time: iso,
            completed: false,
          },
          homeTeam: {
            id: 101,
            school: "Alpha",
            mascot: "Aardvarks",
            abbreviation: "ALP",
            classification: "FBS",
            conference: "Ind",
            color: "#1976d2",
            alternateColor: "#ffffff",
            logos: [],
          },
          awayTeam: {
            id: 102,
            school: "Beta",
            mascot: "Bears",
            abbreviation: "BET",
            classification: "FBS",
            conference: "Ind",
            color: "#d32f2f",
            alternateColor: "#ffffff",
            logos: [],
          },
        },
      ],
    };
  },
};

const LeagueRow: React.FC<{ league: LeagueMini }> = ({ league }) => {
    const theme = useTheme();
  // Hover index for custom weekly area chart
  const weeklyChartContainerRef = React.useRef<HTMLDivElement | null>(null);
  const { getSelectedGames, getPicks, getLeague, getAllPicksForWeek } =
    useLeague();
  const { getYearScores, getWeekScores, getAvailableYearsAndWeeks } =
    useScores();
  const { getCurrentWeek, getPollsAndRecords } = useCfbd();
  const { user } = useAuth0();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<GetSelectedGamesResponse | null>(null);
  const [picksMap, setPicksMap] = React.useState<Record<number, number>>({});
  const [pickerEmailsByGame, setPickerEmailsByGame] = React.useState<
    Record<number, { home: string[]; away: string[] }>
  >({});
  const [pollsData, setPollsData] = React.useState<PollsResponse | null>(null);
  const [recordsData, setRecordsData] = React.useState<RecordsResponse | null>(
    null
  );
  const [yearScore, setYearScore] = React.useState<PlayerScore | undefined>(
    undefined
  );
  const [weekScore, setWeekScore] = React.useState<PlayerScore | undefined>(
    undefined
  );
  const [showScoringLine, setShowScoringLine] = React.useState(false);
  const [showLiveLine, setShowLiveLine] = React.useState(true);
  const [locksEnabled, setLocksEnabled] = React.useState(false);
  const [nailsEnabled, setNailsEnabled] = React.useState(false);
  const [pushEnabled, setPushEnabled] = React.useState(false);
  const [weekTotals, setWeekTotals] = React.useState<{
    correct: number;
    incorrect: number;
  }>({ correct: 0, incorrect: 0 });
  const [seasonTotals, setSeasonTotals] = React.useState<{
    correct: number;
    incorrect: number;
  }>({ correct: 0, incorrect: 0 });
  const [seasonGotwCorrect, setSeasonGotwCorrect] = React.useState(0);
  const [weeklyCorrectCounts, setWeeklyCorrectCounts] = React.useState<number[]>([]);
  const [weeklyNumbers, setWeeklyNumbers] = React.useState<number[]>([]);
  const [currentWeekNumber, setCurrentWeekNumber] = React.useState<number | null>(null);
  const [winningTeamsMap, setWinningTeamsMap] = React.useState<Record<number, number>>({});
  const [weeklyTotalsSelected, setWeeklyTotalsSelected] = React.useState<number[]>([]);
  const [hoverWeekIdx, setHoverWeekIdx] = React.useState<number | null>(null);

  // Helper to derive hover index from mouse X for custom chart
  const deriveHoverIndex = React.useCallback((x: number, width: number, count: number) => {
    const n = Math.max(count - 1, 1);
    return Math.min(count - 1, Math.max(0, Math.round((x / width) * n)));
  }, []);

  // Custom weekly area chart: gradient only under the line, fading to transparent at bottom.
  const WeeklyAreaChart: React.FC<{ values: number[]; hoverIndex: number | null }> = ({ values, hoverIndex }) => {
    const height = 110;
    const padTop = 8; // small top padding so high values not flush against edge
    const padBottom = 4;
    const maxValue = Math.max(...values, 1);
    const width = values.length > 1 ? (values.length - 1) * 40 : 40; // logical width units for smoother curve scaling
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    // Convert data points to coordinates
    const pts = values.map((v, i) => {
      const x = (i / Math.max(values.length - 1, 1)) * width;
      const y = padTop + (height - padTop - padBottom) * (1 - v / maxValue); // higher value => smaller y
      return { x, y };
    });
    // Build a smoothed path (Catmull-Rom -> Bezier approximation)
    const buildSmoothPath = (points: {x:number;y:number}[]) => {
      if (points.length === 0) return '';
      if (points.length === 1) return `M ${points[0].x},${points[0].y}`;
      let d = `M ${points[0].x},${points[0].y}`;
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i === 0 ? i : i - 1];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
      }
      return d;
    };
    const linePath = buildSmoothPath(pts);
    // Area path: from line down to bottom
    const areaPath = `${linePath} L ${pts[pts.length - 1].x},${height} L ${pts[0].x},${height} Z`;
    const hoverPoint = hoverIndex != null ? pts[hoverIndex] : null;
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMinYMid meet"
        style={{ maxWidth: '100%' }}
      >
        <defs>
          <linearGradient id="weeklyUnderLineGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.palette.success.main} stopOpacity={0.55} />
            <stop offset="65%" stopColor={theme.palette.success.main} stopOpacity={0.15} />
            <stop offset="100%" stopColor={theme.palette.success.main} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#weeklyUnderLineGradient)" stroke="none" />
        <path d={linePath} fill="none" stroke={theme.palette.success.main} strokeWidth={2} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {/* Minimal axis baseline */}
        <line x1={0} y1={height - 0.5} x2={width} y2={height - 0.5} stroke={theme.palette.divider} strokeWidth={1} strokeOpacity={0.5} />
        {/* Tick marks (omit first/last) */}
        {pts.map((p, i) => (
          i === 0 || i === pts.length - 1 ? null : (
            <line
              key={`tick-${i}`}
              x1={p.x}
              y1={height - 0.5}
              x2={p.x}
              y2={height - 6}
              stroke={theme.palette.divider}
              strokeWidth={1}
              strokeOpacity={0.3}
            />
          )
        ))}
        {hoverPoint && (
          <circle cx={hoverPoint.x} cy={hoverPoint.y} r={isDesktop ? 7 : 5} fill={theme.palette.success.light || theme.palette.success.main} stroke={theme.palette.success.main} strokeWidth={2} />
        )}
      </svg>
    );
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const current = await getCurrentWeek();
        if (current instanceof Error) throw current;
        setCurrentWeekNumber(current.week);
        const resp = await getSelectedGames(league.id, current.year, current.week);
        if (cancelled) return;
        let selectedForWeek: GetSelectedGamesResponse;
        if (resp instanceof Error) {
          // Dev fallback: provide fake data to hack UI design
          const fake = FakeData.selectedGames();
          setData(fake);
          selectedForWeek = fake;
        } else {
          setData(resp);
          selectedForWeek = resp;
        }
        const p = await getPicks(league.id);
        if (!cancelled && !(p instanceof Error)) {
          const map: Record<number, number> = {};
          p.picks.forEach((pg) => (map[pg.gameId] = pg.pick));
          setPicksMap(map);
        }
        // Fetch all visible picks for the week to build avatar stacks
        const allPicks = await getAllPicksForWeek(
          league.id,
          current.year,
          current.week
        );
        if (!cancelled && !(allPicks instanceof Error)) {
          // allPicks.picks is Record<string, PickedGame[]> keyed by user email
          // Build a map of gameId -> {home: string[]; away: string[]}
          const gamePickers: Record<
            number,
            { home: string[]; away: string[] }
          > = {};
          const emails = Object.keys(allPicks.picks || {});
          for (const email of emails) {
            const picks = allPicks.picks[email] || [];
            for (const pg of picks) {
              if (!gamePickers[pg.gameId])
                gamePickers[pg.gameId] = { home: [], away: [] };
              // Determine which side this pick corresponds to using selected games data
              const sg = selectedForWeek.games.find(
                (g) => g.game.id === pg.gameId
              );
              if (sg) {
                if (pg.pick === sg.homeTeam.id)
                  gamePickers[pg.gameId].home.push(email);
                else if (pg.pick === sg.awayTeam.id)
                  gamePickers[pg.gameId].away.push(email);
              }
            }
          }
          setPickerEmailsByGame(gamePickers);
        }
        // League rules: determine which lines to show
        const lg = await getLeague(league.id);
        if (!cancelled && !(lg instanceof Error)) {
          const fl = lg.rules?.flavorLinesRules;
          const customOn = !!fl?.customGameLinesEnabled;
          const hideOfficial = !!fl?.hideOfficialLinesWhenCustomEnabled;
          setShowScoringLine(customOn);
          setShowLiveLine(!(customOn && hideOfficial));
          // Feature flags from league rules
          setLocksEnabled(!!lg.rules?.lockRules);
          setNailsEnabled(!!lg.rules?.nailRules?.trackNailsSeparately);
          setPushEnabled(!!lg.rules?.pushEnabled);
        } else if (!cancelled) {
          // Default behavior if rules unavailable: show only Live line
          setShowScoringLine(false);
          setShowLiveLine(true);
          setLocksEnabled(false);
          setNailsEnabled(false);
          setPushEnabled(false);
        }
        // Polls + Records for teams in this league's selected games (for rank badges and records row)
        try {
          const teamIds: number[] = [];
          for (const g of selectedForWeek.games) {
            if (g.homeTeam?.id) teamIds.push(g.homeTeam.id);
            if (g.awayTeam?.id) teamIds.push(g.awayTeam.id);
          }
          // Deduplicate
          const uniqueTeamIds = Array.from(new Set(teamIds));
          if (uniqueTeamIds.length > 0) {
            const pr = await getPollsAndRecords(
              selectedForWeek.year,
              selectedForWeek.week,
              uniqueTeamIds
            );
            if (!cancelled && !(pr instanceof Error)) {
              setPollsData(pr.polls || null);
              setRecordsData(pr.records || null);
            }
          }
        } catch (e) {
          // Non-fatal; keep dashboard rendering without polls/records
          if (!cancelled) {
            setPollsData(null);
            setRecordsData(null);
          }
        }
        // Scores (year + week) for current user
        const [ys, ws] = await Promise.all([
          getYearScores(league.id, current.year),
          getWeekScores(league.id, current.year, current.week),
        ]);
        if (!cancelled) {
          const email = user?.email?.toLowerCase();
          if (!(ys instanceof Error) && email) {
            setYearScore(
              ys.playerScores.find((ps) => ps.email.toLowerCase() === email)
            );
          }
          if (!(ws instanceof Error) && email) {
            setWeekScore(
              ws.playerScores.find((ps) => ps.email.toLowerCase() === email)
            );
            // Compute week totals using winningTeams length for decided games
            try {
              const decided = Object.keys(ws.winningTeams || {}).length;
              const correct =
                ws.playerScores.find((ps) => ps.email.toLowerCase() === email)
                  ?.correctGames || 0;
              setWeekTotals({
                correct,
                incorrect: Math.max(decided - correct, 0),
              });
              const wt: Record<number, number> = {};
              Object.keys(ws.winningTeams || {}).forEach((gid) => {
                const num = parseInt(gid, 10);
                if (!isNaN(num)) wt[num] = (ws.winningTeams as any)[gid];
              });
              setWinningTeamsMap(wt);
            } catch {}
          }
          // Compute season totals: sum weekly decided games and correct games for this user
          try {
            const avail = await getAvailableYearsAndWeeks(league.id);
            if (!(avail instanceof Error)) {
              const weeks =
                (avail.availableScores &&
                  avail.availableScores[current.year]) ||
                [];
              if (weeks.length > 0 && email) {
                const weekly = await Promise.all(
                  weeks.map(async (w) => {
                    const r = await getWeekScores(league.id, current.year, w);
                    return r instanceof Error ? null : r;
                  })
                );
                let decidedTotal = 0;
                let correctTotal = 0;
                let gotwTotal = 0;
                const correctCounts: number[] = [];
                const weekNums: number[] = [];
                for (const r of weekly) {
                  if (!r) continue;
                  decidedTotal += Object.keys(r.winningTeams || {}).length;
                  const me = r.playerScores.find(
                    (ps) => ps.email.toLowerCase() === email
                  );
                  if (me) {
                    correctTotal += me.correctGames || 0;
                    gotwTotal += me.correctGamesOfTheWeek || 0;
                    correctCounts.push(me.correctGames || 0);
                    const idx = weekly.indexOf(r);
                    weekNums.push(weeks[idx]);
                  }
                }
                setSeasonTotals({
                  correct: correctTotal,
                  incorrect: Math.max(decidedTotal - correctTotal, 0),
                });
                setSeasonGotwCorrect(gotwTotal);
                setWeeklyCorrectCounts(correctCounts);
                setWeeklyNumbers(weekNums);
                // Also gather total selected games per week for tooltip
                const selectedCounts = await Promise.all(
                  weeks.map(async (w) => {
                    const sg = await getSelectedGames(league.id, current.year, w);
                    if (sg instanceof Error) return 0;
                    return sg.games?.length || 0;
                  })
                );
                setWeeklyTotalsSelected(selectedCounts);
              }
            }
          } catch {}
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || "Failed to load league games");
          // Also show fake to enable visual testing
          setData(FakeData.selectedGames());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    getSelectedGames,
    getCurrentWeek,
    getPicks,
    getLeague,
    getAllPicksForWeek,
    getYearScores,
    getWeekScores,
    getAvailableYearsAndWeeks,
    getPollsAndRecords,
    user?.email,
    league.id,
  ]);

  if (loading && !data) {
    return (
      <Box sx={{ p: 1 }}>
        <Skeleton variant="text" width={180} />
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" width={260} height={140} />
          ))}
        </Box>
      </Box>
    );
  }

  if (!data || data.games.length === 0) {
    return null;
  }

  const liveCount = data.games.filter(
    (g) => !g.game.completed && isGameStarted(g.game.time)
  ).length;
  const totalGames = data.games.length;
  const picksMade = Object.values(picksMap).filter(
    (v) => typeof v === "number"
  ).length;
  const picksComplete = totalGames > 0 && picksMade >= totalGames;

  const StatRow: React.FC<{
    label: string;
    score?: PlayerScore;
    correct?: number;
    incorrect?: number;
  }> = ({ label, score, correct, incorrect }) => {
    const shownCorrect = typeof correct === "number" ? correct : score?.correctGames;
    // Treat explicit override first; otherwise rely on score.losses
    const shownIncorrect = typeof incorrect === "number" ? incorrect : score?.losses;
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Chip size="small" label={label} sx={{ fontWeight: 700 }} />
        <Chip size="small" label={`Score: ${score?.points ?? "—"}`} />
        <Chip size="small" label={`Correct: ${shownCorrect ?? "—"}`} />
        <Chip size="small" label={`Incorrect: ${shownIncorrect ?? "—"}`} />
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} alignItems="flex-start">
        {/* Left column: league name, chips, stats, counters */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {league.name}
            </Typography>
            {data.ready && (
              <Chip
                size="small"
                label="Games Ready"
                color="success"
                icon={<CheckCircleOutlineIcon fontSize="small" />}
              />
            )}
            {picksComplete ? (
              <Chip
                size="small"
                label="Picks Ready"
                color="success"
                icon={<CheckCircleOutlineIcon fontSize="small" />}
              />
            ) : (
              <Chip
                size="small"
                label={`Picks Needed ${Math.max(totalGames - picksMade, 0)}`}
                color="warning"
                icon={<HourglassBottomIcon fontSize="small" />}
              />
            )}
            {liveCount > 0 && (
              <Chip size="small" label={`${liveCount} Live`} color="primary" />
            )}
          </Box>
          {/* Stats summary */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
            <StatRow label="Season" score={yearScore} correct={seasonTotals.correct} incorrect={seasonTotals.incorrect} />
            <StatRow label="Week" score={weekScore} correct={weekTotals.correct} incorrect={weekTotals.incorrect} />
          </Box>
          {/* Fun counters: crowns/clowns/locks/nails based on Year totals */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: { xs: "nowrap", md: "wrap" },
              overflowX: { xs: "auto", md: "visible" },
              mb: { xs: 1, md: 0 },
              px: { xs: 0.5, md: 0 },
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.25)',
                borderRadius: 999,
              },
              '& .MuiChip-root': { flex: '0 0 auto' },
            }}
          >
            <Tooltip title="Crown = Wins (season-to-date)">
              <Chip size="small" label={`👑 ${yearScore?.wins ?? 0}`} sx={{ px: 0.5, "& .MuiChip-label": { fontSize: "1.4em", lineHeight: 1 } }} />
            </Tooltip>
            <Tooltip title="Clown = Losses (season-to-date)">
              <Chip size="small" label={`🤡 ${yearScore?.losses ?? 0}`} sx={{ px: 0.5, "& .MuiChip-label": { fontSize: "1.4em", lineHeight: 1 } }} />
            </Tooltip>
            {locksEnabled && (
              <Tooltip title="Lock = Locks used (season-to-date)">
                <Chip size="small" label={`🔒 ${yearScore?.locks ?? 0}`} sx={{ px: 0.5, "& .MuiChip-label": { fontSize: "1.4em", lineHeight: 1 } }} />
              </Tooltip>
            )}
            {nailsEnabled && (
              <Tooltip title="Nail = Lone picks (season-to-date)">
                <Chip size="small" label={`🎯 ${yearScore?.nails ?? 0}`} sx={{ px: 0.5, "& .MuiChip-label": { fontSize: "1.4em", lineHeight: 1 } }} />
              </Tooltip>
            )}
            <Tooltip title="Game of the Week correct picks (season-to-date)">
              <Chip size="small" label={`🏆 ${seasonGotwCorrect}`} sx={{ px: 0.5, "& .MuiChip-label": { fontSize: "1.4em", lineHeight: 1 } }} />
            </Tooltip>
            {typeof yearScore?.rank === "number" && (
              (() => {
                const r = yearScore!.rank!;
                if (r === 1) return <Chip size="small" label="🥇" sx={{ px: 0.5, "& .MuiChip-label": { fontSize: "1.4em", lineHeight: 1 } }} />;
                if (r === 2) return <Chip size="small" label="🥈" sx={{ px: 0.5, "& .MuiChip-label": { fontSize: "1.4em", lineHeight: 1 } }} />;
                if (r === 3) return <Chip size="small" label="🥉" sx={{ px: 0.5, "& .MuiChip-label": { fontSize: "1.4em", lineHeight: 1 } }} />;
                return <Chip size="small" label={`🏅 ${r}`} sx={{ px: 0.5, "& .MuiChip-label": { fontSize: "1.4em", lineHeight: 1 } }} />;
              })()
            )}
          </Box>
        </Grid>

        {/* Right column: Week/Season pies and Open League button */}
        <Grid item xs={12} md={4}>
          {/* Top-right action */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              mb: 1,
            }}
          >
            <Button
              component={Link}
              to={`/leagues/${league.id}`}
              size="small"
              variant="contained"
            >
              Open League
            </Button>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              justifyItems: { xs: "stretch", md: "end" },
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
                Weekly Correct/Total
              </Typography>
              {weeklyCorrectCounts.length > 0 ? (
                <Tooltip
                  followCursor
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: (t) => ({
                        bgcolor: t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.background.paper,
                        color: t.palette.text.primary,
                        boxShadow: 3,
                        borderRadius: 1,
                        p: 0.75,
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        minWidth: 'unset',
                        border: `1px solid ${t.palette.divider}`,
                      }),
                    },
                  }}
                  title={(() => {
                    const idx = hoverWeekIdx ?? (weeklyNumbers.length - 1);
                    const wk = weeklyNumbers[idx] ?? currentWeekNumber ?? 0;
                    const corr = weeklyCorrectCounts[idx] ?? 0;
                    const tot = weeklyTotalsSelected[idx] ?? totalGames;
                    const pct = tot > 0 ? ((corr / tot) * 100).toFixed(0) : '0';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{`Wk${wk} ${corr}/${tot} (${pct}%)`}</Typography>
                      </Box>
                    );
                  })()}
                >
                  <Box
                    ref={weeklyChartContainerRef}
                    sx={{ position: 'relative', height: 110, width: '100%', display: 'flex', justifyContent: 'center' }}
                    onMouseMove={(e) => {
                      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                      setHoverWeekIdx(deriveHoverIndex(e.clientX - rect.left, rect.width, weeklyCorrectCounts.length));
                    }}
                    onMouseLeave={() => setHoverWeekIdx(null)}
                  >
                    <WeeklyAreaChart values={weeklyCorrectCounts} hoverIndex={hoverWeekIdx} />
                  </Box>
                </Tooltip>
              ) : (
                <Typography variant="caption" color="text.secondary">No picks yet</Typography>
              )}
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
              >
                Season Correct/Incorrect
              </Typography>
              <LeaguePieArc
                title=""
                correct={seasonTotals.correct}
                incorrect={seasonTotals.incorrect}
                push={0}
                showPush={pushEnabled}
                half
                height={110}
                minWidth={140}
                innerRadius={36}
                showLegend={false}
                correctLabel="Correct"
                incorrectLabel="Incorrect"
                pushLabel="Push"
                showBottomPercent
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      {/* Add a tiny margin bump if charts are not showing (e.g., "No picks yet") */}
      {(() => {
        const hasWeekData = weekTotals.correct + weekTotals.incorrect > 0;
        const hasSeasonData = seasonTotals.correct + seasonTotals.incorrect > 0;
        const showCharts = hasWeekData || hasSeasonData;
        return (
          <Box sx={{ mt: showCharts ? 0 : 1 }}>
            <HorizontalScroller>
              {[...data.games]
                .sort((a, b) => {
                  const aLive = !a.game.completed && isGameStarted(a.game.time);
                  const bLive = !b.game.completed && isGameStarted(b.game.time);
                  if (aLive && !bLive) return -1;
                  if (!aLive && bLive) return 1;
                  if (!a.game.completed && b.game.completed) return -1; // upcoming before completed
                  if (a.game.completed && !b.game.completed) return 1;
                  return a.game.id - b.game.id;
                })
                .map((g: SelectedGame) => {
                const isLiveGame =
                  !g.game.completed && isGameStarted(g.game.time);
                const pickers = pickerEmailsByGame[g.game.id] || {
                  home: [],
                  away: [],
                };
                let finalDisplayMode: "normal" | "in_progress" | "correct" | "incorrect" | "push" | "no_pick" | "disabled" = "normal";
                if (g.game.completed) {
                  const winning = winningTeamsMap[g.game.id];
                  const picked = picksMap[g.game.id];
                  if (typeof winning === "number") {
                    if (typeof picked === "number") {
                      if (picked === winning) finalDisplayMode = "correct";
                      else if (winning === 0) finalDisplayMode = "push"; // 0 sentinel for push if used
                      else finalDisplayMode = "incorrect";
                    } else finalDisplayMode = "no_pick";
                  } else finalDisplayMode = "disabled";
                } else if (isLiveGame) finalDisplayMode = "in_progress";
                return (
                  <Box key={g.game.id} sx={{ minWidth: 300 }}>
                    <GameCard
                      game={g.game}
                      homeTeam={g.homeTeam}
                      awayTeam={g.awayTeam}
                      metadata={g.metadata}
                      pickedTeamId={picksMap[g.game.id]}
                      showLocks={false}
                      displayMode={finalDisplayMode}
                      isLive={isLiveGame}
                      // Avatar stacks for live games only
                      awayTeamPickers={pickers.away}
                      homeTeamPickers={pickers.home}
                      currentUserEmail={user?.email || undefined}
                      // Poll ranks + Records
                      awayTeamPollRank={getTeamPollRank(
                        g.awayTeam.school,
                        pollsData
                      )}
                      homeTeamPollRank={getTeamPollRank(
                        g.homeTeam.school,
                        pollsData
                      )}
                      awayTeamRecord={getTeamRecord(g.awayTeam.id, recordsData)}
                      homeTeamRecord={getTeamRecord(g.homeTeam.id, recordsData)}
                      // Keep lines simple on dashboard
                      showLines
                      showScoringLine={showScoringLine}
                      showCustomLine={showScoringLine}
                      showLiveLine={showLiveLine}
                      showFlavorText={false}
                      enableEdit={false}
                    />
                  </Box>
                );
              })}
            </HorizontalScroller>
          </Box>
        );
      })()}
      {/* Removed bottom charts for a cleaner dashboard header */}
      {error && (
        <Typography variant="caption" color="text.secondary">
          {error}
        </Typography>
      )}
    </Box>
  );
};

const PlayerDashboard: React.FC = () => {
  const { getLeagues } = useLeague();
  const [leagues, setLeagues] = React.useState<LeagueMini[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const resp = await getLeagues();
      if (cancelled) return;
      if (resp instanceof Error) {
        // Keep UI minimal; log error and show empty state
        console.warn("Failed to load leagues:", resp.message);
      } else {
        setLeagues(resp.leagues || []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [getLeagues]);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Your Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Overview of your leagues, quick links, and recent activity.
      </Typography>

      {/* Per-league selected games rows */}
      <Box sx={{ mt: 3, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          This Week’s Games
        </Typography>
        {leagues.length === 0 && !loading ? (
          <Typography variant="body2" color="text.secondary">
            Join or create a league to see games here.
          </Typography>
        ) : (
          leagues.map((lg) => <LeagueRow key={lg.id} league={lg} />)
        )}
      </Box>

      {/* Placeholder sections for upcoming work */}
      <Divider sx={{ my: 3 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Recent Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              League feed coming soon.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Your Stats
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Season totals and trends coming soon.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlayerDashboard;
